import { useEffect, useState } from "react";
import { LogIn, LogOut, ArrowDownToLine, ArrowUpFromLine, History } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { currentShift, shiftReport } from "@/lib/shift";
import { fmtDate, uuid, vals } from "@/lib/format";
import { AmountDisplay, NumPad } from "@/components/NumPad";
import { BigButton, Chip, inputCls } from "@/components/ui-kit";
import ZReport, { XReportCard } from "@/components/tpv/ZReport";
import TransferTicket from "@/components/tpv/TransferTicket";

function AmountForm({ label, onSubmit, submitLabel, icon: Icon, withReason, testid }) {
  const { t } = useApp();
  const [v, setV] = useState("");
  const [reason, setReason] = useState("");
  return (
    <div className="space-y-2" data-testid={testid}>
      <AmountDisplay value={v} label={label} testid={`${testid}-display`} />
      {withReason && <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("reason")} className={inputCls} data-testid={`${testid}-reason`} />}
      <NumPad value={v} onChange={setV} testid={`${testid}-numpad`} />
      <BigButton onClick={() => { onSubmit(Number(v) || 0, reason); setV(""); setReason(""); }} disabled={withReason && !(Number(v) > 0)} className="w-full" data-testid={`${testid}-submit`}>
        <Icon className="w-5 h-5" />{submitLabel}
      </BigButton>
    </div>
  );
}

export default function ShiftDialog({ open, onClose, initialTab = "x" }) {
  const { t, view, dispatch, staff, lang, money } = useApp();
  const shift = currentShift(view);
  const [tab, setTab] = useState("x");
  const [z, setZ] = useState(null);
  useEffect(() => { if (open) { setTab(initialTab); setZ(null); } }, [open, initialTab]);
  const rep = shift ? shiftReport(view, shift) : null;
  const history = vals(view.shifts).filter((s) => s.status === "closed").sort((a, b) => b.closed_at.localeCompare(a.closed_at)).slice(0, 6);

  const openShift = (amount) => {
    if (dispatch("shift.open", { id: uuid(), opening_float: amount, device_id: localStorage.getItem("sp_device") })) { toast.success(t("shift_opened")); onClose(); }
  };
  const move = (kind) => (amount, reason) => {
    if (!dispatch("cash.move", { id: uuid(), shift_id: shift.id, kind, amount, reason })) return;
    if (kind === "out" && amount > 0) dispatch("vault.move", { id: uuid(), kind: "out", amount, source: "cash_out", shift_id: shift.id, note: reason, ticket_no: `S${Date.now().toString().slice(-6)}`, staff_name: staff.name, created_at: new Date().toISOString() });
    toast.success(t("move_saved")); setTab("x");
  };
  const [confirm, setConfirm] = useState(null);
  const askClose = (counted) => setConfirm({ counted: Number(counted) || 0, keep: Math.min(Number(counted) || 0, shift.opening_float || 0), transfer: Math.max(0, (Number(counted) || 0) - (shift.opening_float || 0)) });
  const close = () => {
    const { counted, transfer: amount } = confirm;
    const data = { id: shift.id, counted, expected: rep.expected, report: rep };
    if (!dispatch("shift.close", data)) return;
    const limit = Number(view.shop?.cash_diff_alert) || 0;
    const diff = counted - rep.expected;
    if (limit > 0 && Math.abs(diff) > limit) toast.warning(t("cash_diff_alert_msg", { diff: money(diff), limit: money(limit) }), { duration: 12000 });
    toast.success(t("shift_closed"));
    const closedAt = new Date().toISOString();
    let transfer = null;
    if (amount > 0) {
      transfer = { id: uuid(), kind: "in", amount, source: "shift_close", shift_id: shift.id, ticket_no: `T${Date.now().toString().slice(-6)}`, note: `${t("shift")} ${(shift.opened_at || "").slice(0, 10)}`, staff_name: staff.name, created_at: closedAt };
      if (!dispatch("vault.move", transfer)) transfer = null;
    }
    setConfirm(null);
    setZ({ ...shift, ...data, closed_at: closedAt, closed_by: staff.name, transfer });
  };

  let body;
  if (z) {
    body = (
      <div className="space-y-3">
        {z.transfer && <TransferTicket move={z.transfer} />}
        <ZReport shift={z} />
      </div>
    );
  } else if (!shift) {
    body = (
      <div className="space-y-4">
        <AmountForm label={t("opening_float")} submitLabel={t("open_shift")} icon={LogIn} onSubmit={openShift} testid="shift-open" />
        {history.length > 0 && (
          <div className="space-y-1 no-print">
            <div className="font-bold flex items-center gap-1.5"><History className="w-4 h-4" />{t("shift_history")}</div>
            {history.map((h) => (
              <button key={h.id} type="button" onClick={() => setZ(h)} className="w-full text-start rounded-lg bg-secondary px-3 py-2 text-sm" data-testid="shift-history-row">
                {fmtDate(h.opened_at, lang)} → {fmtDate(h.closed_at, lang)} · {h.closed_by}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  } else {
    body = (
      <div className="space-y-3">
        <div className="text-sm text-muted-foreground" data-testid="shift-open-info">{t("shift_open_since")} {fmtDate(shift.opened_at, lang)} · {shift.opened_by}</div>
        <div className="flex gap-2 flex-wrap no-print">
          <Chip active={tab === "x"} onClick={() => setTab("x")} data-testid="shift-tab-x">{t("x_report")}</Chip>
          <Chip active={tab === "in"} onClick={() => setTab("in")} data-testid="shift-tab-in"><ArrowDownToLine className="w-4 h-4" />{t("cash_in")}</Chip>
          <Chip active={tab === "out"} onClick={() => setTab("out")} data-testid="shift-tab-out"><ArrowUpFromLine className="w-4 h-4" />{t("cash_out")}</Chip>
          <Chip active={tab === "close"} onClick={() => setTab("close")} data-testid="shift-tab-close"><LogOut className="w-4 h-4" />{t("close_shift")}</Chip>
        </div>
        {tab === "x" && <XReportCard r={rep} shift={shift} />}
        {tab === "in" && <AmountForm label={t("cash_in")} submitLabel={t("save")} icon={ArrowDownToLine} withReason onSubmit={move("in")} testid="shift-move-in" />}
        {tab === "out" && <AmountForm label={t("cash_out")} submitLabel={t("save")} icon={ArrowUpFromLine} withReason onSubmit={move("out")} testid="shift-move-out" />}
        {tab === "close" && !confirm && <AmountForm label={`${t("counted_cash")} · ${t("expected_cash")} ${rep.expected}`} submitLabel={t("close_shift")} icon={LogOut} onSubmit={askClose} testid="shift-close" />}
        {tab === "close" && confirm && (
          <div className="rounded-xl border-2 border-[#F28C28] bg-[#FFF9E6] p-4 space-y-2" data-testid="shift-close-confirm">
            <div className="font-bold text-[#5A4300]">{t("confirm_close_title")}</div>
            <div className="flex justify-between text-sm"><span>{t("counted_cash")}</span><b dir="ltr" data-testid="confirm-counted">{money(confirm.counted)}</b></div>
            <div className="flex justify-between text-sm"><span>{t("expected_cash")}</span><b dir="ltr">{money(rep.expected)}</b></div>
            <div className="flex justify-between text-sm"><span>{t("difference")}</span><b dir="ltr" className={confirm.counted - rep.expected < 0 ? "text-[#C7353B]" : "text-[#1F9D55]"}>{money(confirm.counted - rep.expected)}</b></div>
            {Number(view.shop?.cash_diff_alert) > 0 && Math.abs(confirm.counted - rep.expected) > Number(view.shop.cash_diff_alert) && <div className="rounded-lg bg-[#C7353B] text-white text-sm font-bold px-3 py-2" data-testid="confirm-diff-alert">⚠ {t("cash_diff_alert_msg", { diff: money(confirm.counted - rep.expected), limit: money(Number(view.shop.cash_diff_alert)) })}</div>}
            <div className="flex justify-between text-sm border-t border-[#F28C28]/50 pt-2"><span>{t("float_kept")}</span><b dir="ltr" data-testid="confirm-keep">{money(confirm.keep)}</b></div>
            <div className="flex justify-between text-base"><span className="font-bold">{t("transfer_to_vault")}</span><b dir="ltr" className="text-[#1F4E8C] text-xl" data-testid="confirm-transfer">{money(confirm.transfer)}</b></div>
            <p className="text-xs text-[#7A6200]">{t("confirm_close_hint")}</p>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <BigButton variant="soft" onClick={() => setConfirm(null)} data-testid="shift-close-cancel">{t("back")}</BigButton>
              <BigButton variant="green" onClick={close} data-testid="shift-close-confirm-btn"><LogOut className="w-5 h-5" />{t("confirm")}</BigButton>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dlg-full" data-testid="shift-dialog">
        <div className="max-w-md mx-auto w-full space-y-4">
        <DialogTitle className="font-heading text-2xl">{z ? t("z_report") : shift ? t("shift") : t("open_shift")}</DialogTitle>
        {body}
        <BigButton variant="soft" onClick={onClose} className="w-full no-print" data-testid="shift-dialog-close">{t("back")}</BigButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
